import React, { useState } from 'react';
import { Building2, Wrench, Database, X, CheckSquare, FileSpreadsheet, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { saveBlobResponse, blobErrorDetail } from '../lib/download';

export const THEME_KIND = {
  core: { label: 'Kern-Infrastruktur', Icon: Building2, on: 'text-emerald-200 border-emerald-400/70 bg-emerald-500/25', off: 'text-emerald-300/80 border-emerald-500/30 hover:bg-emerald-500/10' },
  adjacent: { label: 'Angrenzende Geschäftsmodelle', Icon: Wrench, on: 'text-sky-200 border-sky-400/70 bg-sky-500/25', off: 'text-sky-300/80 border-sky-500/30 hover:bg-sky-500/10' },
  deal: { label: 'Aus den recherchierten Deals', Icon: Database, on: 'text-amber-200 border-amber-400/70 bg-amber-500/25', off: 'text-amber-300/80 border-amber-500/30 hover:bg-amber-500/10' },
};

// Excel of the research themes — one sheet per fund (all funds) or just this fund (Talu, 30.09.2026)
export function ThemeExportButton({ fundId, label, testid, className = '' }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const res = await api.exportFundThemes(fundId);
      const name = saveBlobResponse(res, fundId ? 'OLTA_Themen.xlsx' : 'OLTA_Fonds-Themen.xlsx');
      const n = res.headers?.['x-fund-count'];
      toast.success(`${name} geladen${n && !fundId ? ` — ${n} Fonds, je ein Tabellenblatt` : ''}`);
    } catch (err) { toast.error((await blobErrorDetail(err)) || 'Export fehlgeschlagen'); }
    finally { setBusy(false); }
  };
  return (
    <button onClick={run} disabled={busy} data-testid={testid} title={fundId ? 'Themen dieses Fonds als Excel' : 'Themen aller recherchierten Fonds als Excel — ein Blatt pro Fonds'}
      className={`inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground disabled:opacity-50 ${className}`}>
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileSpreadsheet className="w-3 h-3" />} {label}
    </button>
  );
}

// Per-fund theme chips: what the research found (core targets, adjacent angles, grouped deal angles) — selecting
// some restricts the next screening to exactly these themes (Talu, 25.09.2026).
export function FundThemeFilter({ options, selected, toggle, clear, selectAll, prefix = 'fund', fundId }) {
  if (!options?.length) return null;
  const sel = new Set(selected);
  const allChosen = options.every(o => sel.has(o.id));
  return (
    <div className="border border-border/60 rounded-sm bg-secondary/20 p-2.5 space-y-2" data-testid={`${prefix}-theme-filter`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[10px] uppercase tracking-widest font-bold text-foreground/80">
          Themen dieses Fonds <span className="text-muted-foreground font-normal normal-case tracking-normal">— gefunden in Profil-Recherche und Deals, ohne Auswahl = alle</span>
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          {selectAll && !allChosen && (
            <button onClick={() => selectAll(options.map(o => o.id))} data-testid={`${prefix}-theme-select-all`}
              className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <CheckSquare className="w-3 h-3" /> Alle {options.length} wählen
            </button>
          )}
          {sel.size > 0 && (
            <button onClick={clear} data-testid={`${prefix}-theme-clear`} className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              <X className="w-3 h-3" /> {sel.size} {sel.size !== 1 ? 'Themen' : 'Thema'} abwählen
            </button>
          )}
          <ThemeExportButton fundId={fundId} label="Excel: dieser Fonds" testid={`${prefix}-theme-export-fund`} />
          <ThemeExportButton label="Excel: alle Fonds (1 Blatt je Fonds)" testid={`${prefix}-theme-export-all`} />
        </div>
      </div>
      {Object.entries(THEME_KIND).map(([kind, k]) => {
        const items = options.filter(o => o.kind === kind);
        if (!items.length) return null;
        const groupAll = items.every(o => sel.has(o.id));
        return (
          <div key={kind} data-testid={`${prefix}-theme-group-${kind}`}>
            <p className="text-[10px] text-muted-foreground inline-flex items-center gap-1 mb-1">
              <k.Icon className="w-3 h-3" /> {k.label} ({items.length})
              {selectAll && (
                <button onClick={() => (groupAll ? items.forEach(o => toggle(o.id)) : selectAll(items.map(o => o.id)))} data-testid={`${prefix}-theme-group-all-${kind}`}
                  className="ml-1 underline underline-offset-2 hover:text-foreground">{groupAll ? 'Gruppe abwählen' : 'alle wählen'}</button>
              )}
            </p>
            <div className="flex flex-wrap gap-1">
              {items.map(o => (
                <button key={o.id} onClick={() => toggle(o.id)} title={[o.description, o.targets?.length ? `aus: ${o.targets.join(', ')}` : ''].filter(Boolean).join('\n')}
                  data-testid={`${prefix}-theme-${o.id}`} aria-pressed={sel.has(o.id)}
                  className={`text-[10px] px-1.5 py-0.5 rounded-sm border transition-colors ${sel.has(o.id) ? k.on : k.off}`}>
                  {o.label}{o.count > 1 ? <span className="opacity-70"> {o.count}×</span> : null}
                </button>
              ))}
            </div>
          </div>
        );
      })}
      {sel.size > 0 && (
        <p className="text-[10px] text-amber-300/90" data-testid={`${prefix}-theme-hint`}>
          Nächstes Screening sucht nur nach den gewählten Themen — Add-on-Treffer zu Portfolio-Firmen entfallen dabei.
        </p>
      )}
    </div>
  );
}
